import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface CartItem {
  id: number | string;
  name: string;
  price: number;
  image: string;
  quantity: number;
  variant_id?: number | null;
  specification?: {
    color?: string;
    size?: string;
    [key: string]: unknown;
  };
}

function specsMatch(a: CartItem['specification'], b: CartItem['specification']): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  const keysA = Object.keys(a).sort();
  const keysB = Object.keys(b).sort();
  if (keysA.length !== keysB.length) return false;
  return keysA.every((k) => a[k] === b[k]);
}

interface CartState {
  items: CartItem[];
  isOpen: boolean;
  hydrated: boolean;
  lastAddedAt: number | null;
  hydrate: () => void;
  addItem: (item: CartItem) => void;
  updateQuantity: (id: number | string, quantity: number) => void;
  updateQuantityForLine: (item: CartItem, quantity: number) => void;
  removeItem: (id: number | string) => void;
  removeLineItem: (item: CartItem) => void;
  clearCart: () => void;
  toggleCart: (force?: boolean) => void;
}

export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      items: [],
      isOpen: false,
      hydrated: false,
      lastAddedAt: null,
      hydrate: () => set({ hydrated: true }),
      addItem: (item) =>
        set((state) => {
          const existing = state.items.find(
            (i) => i.id === item.id && specsMatch(i.specification, item.specification)
          );
          const items = existing
            ? state.items.map((i) =>
                i.id === item.id && specsMatch(i.specification, item.specification)
                  ? { ...i, quantity: i.quantity + item.quantity }
                  : i
              )
            : [...state.items, { ...item, quantity: item.quantity || 1 }];
          return {
            items,
            lastAddedAt: Date.now(),
            isOpen: true,
          };
        }),
      updateQuantity: (id, quantity) =>
        set((state) => ({
          items:
            quantity <= 0
              ? state.items.filter((i) => i.id !== id)
              : state.items.map((i) => (i.id === id ? { ...i, quantity } : i)),
        })),
      updateQuantityForLine: (item, quantity) =>
        set((state) => ({
          items:
            quantity <= 0
              ? state.items.filter(
                  (i) =>
                    !(i.id === item.id && specsMatch(i.specification, item.specification))
                )
              : state.items.map((i) =>
                  i.id === item.id && specsMatch(i.specification, item.specification)
                    ? { ...i, quantity }
                    : i
                ),
        })),
      removeItem: (id) =>
        set((state) => ({
          items: state.items.filter((i) => i.id !== id),
        })),
      removeLineItem: (item) =>
        set((state) => ({
          items: state.items.filter(
            (i) =>
              !(i.id === item.id && specsMatch(i.specification, item.specification))
          ),
        })),
      clearCart: () => set({ items: [], isOpen: false }),
      toggleCart: (force) =>
        set((state) => ({
          isOpen: typeof force === 'boolean' ? force : !state.isOpen,
        })),
    }),
    {
      name: 'cart-storage',
      partialize: (state) => ({ items: state.items }),
      onRehydrateStorage: () => (state) => {
        if (state) state.hydrate();
      },
    }
  )
);
